import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import Right from "./Right";
import { useDispatch, useSelector } from "react-redux";
import { fetchMatches } from "../../redux/matchesSlice";
import { useEffect, useMemo, useState } from "react";
import { calculateAge, getUserIdFromToken } from "../../utils/auth";
import {
  addShortlist,
  removeShortlist,
  fetchShortlist,
} from "../../redux/shortlistSlice";
import { fetchRequests } from "../../redux/requestsSlice";
import {
  requestPhoto,
  deletePhotoRequest,
  fetchPhotoRequests,
} from "../../redux/photoRequestSlice";
import { server } from "../../server";

function Matches() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const filter = location.state?.profileId;
  const { profiles, loading } = useSelector((state) => state.matches);
  const shortlisted = useSelector((state) => state.shortlist?.shortlists || []);
  const photoRequests = useSelector(
    (state) => state.photoRequest.photoRequests || [],
  );
  const { sentRequests = [], receivedRequests = [] } = useSelector(
    (state) => state.requests || {},
  );

  const [userId, setUserId] = useState(null);
  const [shortlistLoadingIds, setShortlistLoadingIds] = useState([]);
  const [requestLoadingIds, setRequestLoadingIds] = useState([]);
  const [photoRequestedIds, setPhotoRequestedIds] = useState([]);
  const [photoRequestLoadingIds, setPhotoRequestLoadingIds] = useState([]);
  const [viewedProfileIds, setViewedProfileIds] = useState(
    JSON.parse(localStorage.getItem("VIEWED_MATCHES")) || [],
  );
  const [activeTab, setActiveTab] = useState("all");
  const getId = (v) => (typeof v === "string" ? v : v?._id);
  const [storedFilters, setStoredFilters] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem("filter");
    if (raw) {
      const parsed = JSON.parse(raw);
      setStoredFilters(parsed.filter ?? null);
    } else {
      setStoredFilters(null);
    }
  }, []);

  const filteredProfiles = useMemo(() => {
    if (!storedFilters) return profiles;

    return profiles.filter((profile) => {
      const age = calculateAge(profile.profile?.dob);

      if (
        storedFilters.ageRange &&
        (age < storedFilters.ageRange[0] || age > storedFilters.ageRange[1])
      )
        return false;

      const checkField = (filterField, value) => {
        if (!filterField || filterField.includes("Open to All")) return true;
        if (!value) return false;
        return filterField
          .map((f) => f.toString().toLowerCase())
          .includes(value?.toString().toLowerCase());
      };

      const fields = [
        "religion",
        "community",
        "motherTongue",
        "qualification",
        "workingIn",
        "profession",
        "state",
        "maritalStatus",
        "diet",
      ];
      for (const key of fields) {
        if (!checkField(storedFilters[key], profile.profile?.[key]))
          return false;
      }

      return true;
    });
  }, [profiles, storedFilters]);

  // Fetch userId and all dependent data once
  useEffect(() => {
    const fetchUserData = async () => {
      const uid = await getUserIdFromToken();
      if (!uid) return;
      setUserId(uid);
      dispatch(fetchMatches());
      dispatch(fetchRequests(uid));
      dispatch(fetchShortlist(uid));
      dispatch(fetchPhotoRequests(uid));
    };
    fetchUserData();
  }, [dispatch]);

  const NEW_MATCH_LIMIT = 20;

  // NEW matches
  const newMatches = filteredProfiles
    .slice(0, NEW_MATCH_LIMIT)
    .filter((p) => !viewedProfileIds.includes(p._id));

  // ALL matches
  const allMatches = filteredProfiles.filter(
    (p, index) => index >= NEW_MATCH_LIMIT || viewedProfileIds.includes(p._id),
  );

  const markProfileViewed = (profileId) => {
    setViewedProfileIds((prev) => {
      if (prev.includes(profileId)) return prev;
      return [...prev, profileId];
    });
  };

  // Persist viewed profiles
  useEffect(() => {
    localStorage.setItem("VIEWED_MATCHES", JSON.stringify(viewedProfileIds));
  }, [viewedProfileIds]);

  // Update photoRequestedIds
  useEffect(() => {
    setPhotoRequestedIds(photoRequests.map((r) => r.requestedProfileId));
  }, [photoRequests]);

  // Re-fetch on window focus
  useEffect(() => {
    const handleFocus = () => {
      if (!userId) return;
      dispatch(fetchRequests(userId));
      dispatch(fetchShortlist(userId));
      dispatch(fetchPhotoRequests(userId));
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [userId, dispatch]);

  // Shortlist toggle
  const handleShortlistToggle = async (profileId) => {
    setShortlistLoadingIds((prev) => [...prev, profileId]);
    try {
      const existing = shortlisted.find(
        (s) => s.shortlistedProfileId === profileId,
      );
      if (existing?._id) {
        await dispatch(removeShortlist(existing._id)).unwrap();
      } else {
        await dispatch(
          addShortlist({ userId, shortlistedProfileId: profileId }),
        ).unwrap();
      }
      dispatch(fetchShortlist(userId));
    } finally {
      setShortlistLoadingIds((prev) => prev.filter((id) => id !== profileId));
    }
  };

  // Requests
  const handleSendRequest = async (receiverId) => {
    setRequestLoadingIds((prev) => [...prev, receiverId]);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${server}/requests/SendRequest`, {
        method: "POST",
        headers: { Authorization: token, "Content-Type": "application/json" },
        body: JSON.stringify({ senderId: userId, receiverId }),
      });
      if (!res.ok) throw new Error("Failed to send request");
      dispatch(fetchRequests(userId));
    } finally {
      setRequestLoadingIds((prev) => prev.filter((id) => id !== receiverId));
    }
  };

  const handleCancelRequest = async (receiverId) => {
    setRequestLoadingIds((prev) => [...prev, receiverId]);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `${server}/requests/CancelRequest?userId=${userId}&receiverId=${receiverId}`,
        {
          method: "DELETE",
          headers: { Authorization: token },
        },
      );
      if (!res.ok) throw new Error("Failed to cancel request");
      dispatch(fetchRequests(userId));
    } finally {
      setRequestLoadingIds((prev) => prev.filter((id) => id !== receiverId));
    }
  };

  const handleAcceptRequest = async (senderId) => {
    setRequestLoadingIds((prev) => [...prev, senderId]);
    try {
      const token = localStorage.getItem("authToken");
      await fetch(`${server}/requests/AcceptRequest`, {
        method: "POST",
        headers: { Authorization: token, "Content-Type": "application/json" },
        body: JSON.stringify({ senderId, receiverId: userId }),
      });
      dispatch(fetchRequests(userId));
    } finally {
      setRequestLoadingIds((prev) => prev.filter((id) => id !== senderId));
    }
  };

  // Photo Requests
  const handleRequestPhoto = async (profileId) => {
    setPhotoRequestLoadingIds((prev) => [...prev, profileId]);
    try {
      await dispatch(
        requestPhoto({ userId, requestedProfileId: profileId }),
      ).unwrap();
    } finally {
      setPhotoRequestLoadingIds((prev) =>
        prev.filter((id) => id !== profileId),
      );
    }
  };

  const handleCancelPhotoRequest = async (profileId) => {
    const item = photoRequests.find((r) => r.requestedProfileId === profileId);
    if (!item?._id) return;
    setPhotoRequestLoadingIds((prev) => [...prev, profileId]);
    try {
      await dispatch(deletePhotoRequest({ id: item._id, userId })).unwrap();
    } finally {
      setPhotoRequestLoadingIds((prev) =>
        prev.filter((id) => id !== profileId),
      );
    }
  };

  // Add this useEffect after your existing useEffects
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const visibleProfiles = activeTab === "new" ? newMatches : allMatches;

  return (
    <>
      <Header />
      <section>
        <div className="container pt-3 pb-4">
          <div className="row g-3">
            <div className="col-lg-8 col-12">
              <div className="bg-white shadow rounded-pill mb-3 p-2 sticky-top header-gap z-1">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Link
                      to="#"
                      onClick={() => setActiveTab("all")}
                      className={`btn pill-py-2 fw-normal px-lg-5 px-4 rounded-end-0 rounded-start-pill ${
                        activeTab === "all"
                          ? "bg-light-red border border-danger text-dark"
                          : "bg-white border border-light text-dark"
                      }  `}
                    >
                      All
                    </Link>

                    <Link
                      to="#"
                      onClick={() => setActiveTab("new")}
                      className={`btn pill-py-2 fw-normal px-lg-5 px-4 rounded-start-0 rounded-end-pill ${
                        activeTab === "new"
                          ? "bg-light-red border border-danger text-dark"
                          : "bg-white border border-light text-dark"
                      }  `}
                    >
                      <i className="bi bi-collection me-1"></i> New Matches (20)
                    </Link>

                    <Link
                      to="/search"
                      className="btn border border-light pill-py-2 text-dark fw-normal rounded-pill px-3 ms-2"
                    >
                      <i className="bi bi-search"></i>
                    </Link>
                  </div>
                  <div>
                    <Link
                      to="/filter"
                      className="btn btn-dark pill-py-2 text-dark fw-normal rounded-pill px-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="text-white"
                      >
                        <path d="M19,2H5A3,3,0,0,0,2,5V6.17a3,3,0,0,0,.25,1.2l0,.06a2.81,2.81,0,0,0,.59.86L9,14.41V21a1,1,0,0,0,.47.85A1,1,0,0,0,10,22a1,1,0,0,0,.45-.11l4-2A1,1,0,0,0,15,19V14.41l6.12-6.12a2.81,2.81,0,0,0,.59-.86l0-.06A3,3,0,0,0,22,6.17V5A3,3,0,0,0,19,2ZM13.29,13.29A1,1,0,0,0,13,14v4.38l-2,1V14a1,1,0,0,0-.29-.71L5.41,8H18.59ZM20,6H4V5A1,1,0,0,1,5,4H19a1,1,0,0,1,1,1Z"></path>
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>

              <div className="row g-2">
                {loading && (
                  <div className="d-flex flex-column justify-content-center align-items-center">
                    <div
                      className="spinner-border text-red-primary mt-4"
                      role="status"
                    ></div>
                  </div>
                )}

                {!loading &&
                  visibleProfiles?.map((item) => {
                    const isShortlisted = shortlisted.some(
                      (s) => s.shortlistedProfileId === item._id,
                    );

                    const isPhotoRequested = photoRequestedIds.includes(
                      item._id,
                    );
                    const requestLoading = requestLoadingIds.includes(item._id);
                    const shortlistLoading = shortlistLoadingIds.includes(
                      item._id,
                    );
                    const photoLoading = photoRequestLoadingIds.includes(
                      item._id,
                    );

                    const hasSentRequest = sentRequests.find(
                      (r) =>
                        r.receiverId === item._id &&
                        r.status?.toLowerCase() === "pending",
                    );

                    const hasReceivedRequest = receivedRequests.find(
                      (r) =>
                        getId(r.senderId) === item._id &&
                        r.status?.toLowerCase() === "pending",
                    );

                    const acceptedRequest = [
                      ...sentRequests,
                      ...receivedRequests,
                    ].find(
                      (r) =>
                        r.status?.toLowerCase() === "accepted" &&
                        (getId(r.senderId) === item._id ||
                          getId(r.receiverId) === item._id),
                    );

                    const age = calculateAge(item.profile?.dob);

                    const imageUrl =
                      item.images?.length > 0
                        ? item.images[0].docUrl
                        : "/assets/images/default-profile.jpg";

                    return (
                      <div className="col-lg-4 col-md-6 col-12" key={item._id}>
                        <div
                          onClick={() => {
                            markProfileViewed(item._id);
                            navigate("/profile", {
                              state: { profileId: item._id },
                            });
                          }}
                          className="position-relative rounded-4 d-flex align-items-end z-0 cursor-pointer"
                        >
                          <div className="position-relative rounded-4 d-flex align-items-end z--1 cursor-pointer w-100">
                            <img
                              src={imageUrl}
                              className="w-100 rounded-4 shadow position-relative z-1 bg-transparent object-fit-contain"
                              height="400"
                              alt=""
                            />
                            <img
                              src={imageUrl}
                              className="w-100 rounded-4 position-absolute top-0 start-0 z-0 object-fit-cover"
                              height="400"
                              alt=""
                            />
                          </div>

                          <div className="position-absolute p-3 dark-gradient-home w-100 rounded-4 d-flex justify-content-between flex-column h-100">
                            <div className="d-flex justify-content-between align-items-center">
                              <p className="text-white m-0 lh-sm fs-6">
                                {item.profile?.city}, {item.profile?.state}
                              </p>
                            </div>

                            <div>
                              <p className="text-uppercase text-white m-0 lh-1 fs-6 fw-semibold">
                                GR{item.userId}
                              </p>

                              <h4 className="fw-medium text-white lh-1 mt-1 mb-0">
                                {item.name}, {age}
                                <img
                                  src={
                                    item.gender === "Female"
                                      ? "https://cdn-icons-png.flaticon.com/512/8207/8207939.png"
                                      : "https://cdn-icons-png.flaticon.com/512/8207/8207927.png"
                                  }
                                  width="18"
                                  className="ms-1"
                                  alt="gender"
                                />
                              </h4>

                              <p className="m-0 text-white fs-6 mt-2">
                                {item.profile?.height}
                                <i className="bi bi-circle-fill fs-dot mx-2"></i>
                                {item.profile?.community}
                              </p>

                              <p className="m-0 text-white fs-6">
                                {item.profile?.profession}
                                <i className="bi bi-circle-fill fs-dot mx-2"></i>
                                {item.profile?.annualIncome}
                              </p>

                              <p className="m-0 text-white fs-6">
                                {item.profile?.qualification}
                                <i className="bi bi-circle-fill fs-dot mx-2"></i>
                                {item.profile?.maritalStatus}
                              </p>

                              <div className="bg-light-gradient-right ms-n4 p-1 my-3">
                                <p className="text-dark m-0 lh-sm fw-normal ms-3 fs-6 fst-italic">
                                  Profile Managed by {item.profileFor}
                                </p>
                              </div>

                              <div className="d-flex justify-content-center gap-3 position-relative z-1">
                                {/* Request Button */}
                                <div className="text-center position-relative z-1 cursor-pointer">
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (requestLoading) return;

                                      if (acceptedRequest) {
                                        navigate(`/chat/${item._id}`);
                                      } else if (hasReceivedRequest) {
                                        handleAcceptRequest(item._id);
                                      } else if (hasSentRequest) {
                                        handleCancelRequest(item._id);
                                      } else {
                                        handleSendRequest(item._id);
                                      }
                                    }}
                                  >
                                    <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                      {requestLoading ? (
                                        <div className="spinner-border spinner-border-sm"></div>
                                      ) : acceptedRequest ? (
                                        <i className="bi bi-chat text-primary"></i>
                                      ) : hasReceivedRequest ? (
                                        <i className="bi bi-check-circle text-primary"></i>
                                      ) : hasSentRequest ? (
                                        <i className="bi bi-x-lg text-primary"></i>
                                      ) : (
                                        <i className="bi bi-envelope-arrow-up text-primary"></i>
                                      )}
                                    </div>
                                    <p className="m-0 text-white fs-6">
                                      {acceptedRequest
                                        ? "Chat"
                                        : hasReceivedRequest
                                          ? "Accept"
                                          : hasSentRequest
                                            ? "Cancel"
                                            : "Request"}
                                    </p>
                                  </span>
                                </div>

                                {/* Shortlist Button */}
                                <div className="text-center position-relative z-1 cursor-pointer">
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShortlistToggle(item._id);
                                    }}
                                  >
                                    {shortlistLoadingIds.includes(item._id) ? (
                                      <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                        <span className="spinner-border spinner-border-sm"></span>
                                      </div>
                                    ) : isShortlisted ? (
                                      <>
                                        <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                          <i className="bi bi-star-fill fs-3 text-primary"></i>
                                        </div>
                                        <p className="m-0 text-white fs-6">
                                          Shortlisted
                                        </p>
                                      </>
                                    ) : (
                                      <>
                                        <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                          <i className="bi bi-star fs-3 text-primary"></i>
                                        </div>
                                        <p className="m-0 text-white fs-6">
                                          Shortlist
                                        </p>
                                      </>
                                    )}
                                  </span>
                                </div>

                                {/* Photo Request */}
                                <div className="text-center position-relative z-1 cursor-pointer">
                                  {item.images?.length > 0 ? (
                                    <Link
                                      to="/images"
                                      state={{ profileId: item?._id }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                        <i className="bi bi-images fs-3 text-primary"></i>
                                      </div>
                                      <p className="m-0 text-white fs-6">
                                        Photo
                                      </p>
                                    </Link>
                                  ) : (
                                    <span
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isPhotoRequested) {
                                          handleCancelPhotoRequest(item._id);
                                        } else {
                                          handleRequestPhoto(item._id);
                                        }
                                      }}
                                    >
                                      {photoLoading ? (
                                        <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                          <span className="spinner-border spinner-border-sm"></span>
                                        </div>
                                      ) : isPhotoRequested ? (
                                        <>
                                          <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                            <i className="bi bi-image-fill fs-3 text-primary"></i>
                                          </div>
                                          <p className="m-0 text-white fs-6">
                                            Requested
                                          </p>
                                        </>
                                      ) : (
                                        <>
                                          <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                            <i className="bi bi-images fs-3 text-primary"></i>
                                          </div>
                                          <p className="m-0 text-white fs-6">
                                            Photo
                                          </p>
                                        </>
                                      )}
                                    </span>
                                  )}
                                </div>

                                {/* Chat */}
                                <div className="text-center position-relative z-1 cursor-pointer">
                                  <Link
                                    to="/chat"
                                    state={{ profileId: item?._id }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="icon-shape icon-lg bg-light-red rounded-circle">
                                      <i className="bi bi-chat fs-3 text-primary"></i>
                                    </div>
                                    <p className="m-0 text-white fs-6">Chat</p>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <Right />
          </div>
        </div>
      </section>
    </>
  );
}

export default Matches;
